import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid2,
  Slider,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";

import {
  InverterCommandResponse,
  InverterResponse,
  useCreateInverterCommand,
  useGetInverterCommands,
  useGetInverters,
} from "api";
import { Widget } from "components/elements/mui";
import { PageLayout } from "components/layouts";
import { useFormatMessage, useLocaleContext } from "lang";
import { DEFAULT } from "routes/consts";
import { getSelectedPowerPlant, useAppSelector } from "store";
import { formatPower } from "utils/energy";

const fmt = (
  value: number | undefined | null,
  unit: string,
  decimals = 2,
) => (value === undefined || value === null ? "—" : `${value.toFixed(decimals)} ${unit}`);

const Row = ({ label, value }: { label: string; value: string }) => (
  <TableRow>
    <TableCell sx={{ py: 0.75, pl: 0, color: "text.secondary" }}>
      {label}
    </TableCell>
    <TableCell sx={{ py: 0.75, pr: 0, textAlign: "right" }}>{value}</TableCell>
  </TableRow>
);

const CommandStatusChip = ({ cmd }: { cmd: InverterCommandResponse }) => {
  if (cmd.executedAt === null) {
    return <Chip size="small" label="pending" color="default" />;
  }
  return (
    <Chip
      size="small"
      color={cmd.succeeded ? "success" : "error"}
      label={cmd.succeeded ? "ok" : cmd.errorMessage ?? "failed"}
    />
  );
};

const describeCommand = (cmd: InverterCommandResponse) => {
  if (cmd.command === "set_power_state") {
    const state = (cmd.params as { state: "on" | "off" }).state;
    return `Power: ${state.toUpperCase()}`;
  }
  if (cmd.command === "set_active_power_limit") {
    const p = (cmd.params as { percent: number }).percent;
    return `Active power limit: ${p}%`;
  }
  return cmd.command;
};

type PendingCommand =
  | { type: "set_power_state"; state: "on" | "off" }
  | { type: "set_active_power_limit"; percent: number };

const describePending = (
  pending: PendingCommand,
  formatMessage: ReturnType<typeof useFormatMessage>,
) => {
  if (pending.type === "set_power_state") {
    return formatMessage("inverter.confirm.powerState", {
      values: { state: pending.state.toUpperCase() },
    });
  }
  return formatMessage("inverter.confirm.powerLimit", {
    values: { percent: pending.percent },
  });
};

type Status = NonNullable<InverterResponse["liveData"]>["status"];

const STATUS_COLOR: Record<Status, "success" | "error" | "default"> = {
  producing: "success",
  offline: "error",
  consuming: "default",
  idle: "default",
};

const InverterPanel = ({
  inverter,
  canEdit,
  liveDataInterval,
  dataUpdatedAt,
}: {
  inverter: InverterResponse;
  canEdit: boolean;
  liveDataInterval: number;
  dataUpdatedAt: number;
}) => {
  const formatMessage = useFormatMessage();
  const { locale } = useLocaleContext();
  const { data: commands, isLoading } = useGetInverterCommands(
    canEdit ? inverter.id : undefined,
  );
  const create = useCreateInverterCommand(inverter.id);

  const live = inverter.liveData;

  // Mirror the PV-level pattern (routes/utils.ts): recompute staleness
  // only when a fetch actually lands, not on every render. Otherwise a
  // backgrounded tab returns with `Date.now()` far ahead of the cached
  // `live.date` and flashes "offline" before the focus refetch finishes.
  const [stale, setStale] = useState(false);
  const staleThreshold = Math.max(4 * liveDataInterval, 60_000);
  useEffect(() => {
    const ts = live?.date ?? inverter.lastSeenAt;
    setStale(
      !ts || Date.now() - new Date(ts).getTime() > staleThreshold,
    );
  }, [dataUpdatedAt, live?.date, inverter.lastSeenAt, staleThreshold]);

  // What we last *commanded* the inverter to do — sourced from the
  // history because the vendor write registers aren't readable. Used as
  // the source of truth when the inverter's live state is ambiguous
  // (idle could be remote-off, weather-idle, or startup-wait) and as
  // the *only* truth when live data is stale.
  const lastSuccessful = useMemo(() => {
    return (cmd: InverterCommandResponse["command"]) =>
      commands?.find((c) => c.command === cmd && c.succeeded === true);
  }, [commands]);
  const lastPowerStateCmd = lastSuccessful("set_power_state");
  const lastPowerLimitCmd = lastSuccessful("set_active_power_limit");
  const lastCommandedOn = lastPowerStateCmd
    ? (lastPowerStateCmd.params as { state: "on" | "off" }).state === "on"
    : undefined;

  // Live producing → definitely ON, regardless of what we commanded last
  // (something may have overridden us). When live data is stale we
  // ignore the cached power reading entirely and trust the last command.
  const livePowerKw = live?.power
    ? live.power.L1 + live.power.L2 + live.power.L3
    : 0;
  const isOn = stale
    ? lastCommandedOn ?? false
    : livePowerKw > 0
      ? true
      : lastCommandedOn ?? live?.status !== "offline";

  const commandedLimit = lastPowerLimitCmd
    ? (lastPowerLimitCmd.params as { percent: number }).percent
    : 100;
  const [limitDraft, setLimitDraft] = useState(commandedLimit);
  const [pending, setPending] = useState<PendingCommand | null>(null);

  const hasPending = useMemo(
    () => commands?.some((c) => c.executedAt === null) ?? false,
    [commands],
  );

  const submit = () => {
    if (!pending) return;
    const body =
      pending.type === "set_power_state"
        ? { command: "set_power_state" as const, params: { state: pending.state } }
        : {
            command: "set_active_power_limit" as const,
            params: { percent: pending.percent },
          };
    create.mutate(body, { onSuccess: () => setPending(null) });
  };

  const displayStatus: Status = stale
    ? "offline"
    : (live?.status as Status) ?? "idle";

  const readings = (
    <Table size="small">
      <TableBody>
        <Row label={formatMessage("inverter.frequency")} value={fmt(live?.frequency, "Hz")} />
        <Row label={formatMessage("inverter.dcPower")} value={fmt(live?.dcPower, "kW")} />
        <Row
          label={formatMessage("inverter.reactivePower")}
          value={fmt(live?.reactivePower, "kVar")}
        />
        <Row
          label={formatMessage("inverter.moduleTemperature")}
          value={fmt(live?.moduleTemperature, "°C", 0)}
        />
        <Row
          label={formatMessage("inverter.inverterTemperature")}
          value={fmt(live?.inverterTemperature, "°C", 0)}
        />
        <Row
          label={formatMessage("inverter.dcBusVoltage")}
          value={fmt(live?.dcBusVoltage, "V", 1)}
        />
        <Row
          label={formatMessage("inverter.insulation")}
          value={fmt(live?.insulationResistance, "kΩ", 0)}
        />
        <Row
          label={formatMessage("inverter.generationTimeToday")}
          value={fmt(live?.generationTimeToday, "min", 0)}
        />
        <Row
          label={formatMessage("inverter.generationTimeTotal")}
          value={fmt(live?.generationTimeTotal, "h", 0)}
        />
      </TableBody>
    </Table>
  );

  const perPhase = (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={{ py: 0.5, pl: 0, color: "text.secondary" }} />
          <TableCell sx={{ py: 0.5, textAlign: "right", color: "text.secondary" }}>
            V
          </TableCell>
          <TableCell sx={{ py: 0.5, textAlign: "right", color: "text.secondary" }}>
            A
          </TableCell>
          <TableCell sx={{ py: 0.5, textAlign: "right", color: "text.secondary" }}>
            kW
          </TableCell>
          <TableCell
            sx={{ py: 0.5, pr: 0, textAlign: "right", color: "text.secondary" }}
          >
            PF
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {(
          [
            ["L1", "R"],
            ["L2", "S"],
            ["L3", "T"],
          ] as const
        ).map(([key, label]) => (
          <TableRow key={key}>
            <TableCell sx={{ py: 0.75, pl: 0, color: "text.secondary" }}>
              {label}
            </TableCell>
            <TableCell sx={{ py: 0.75, textAlign: "right" }}>
              {fmt(live?.voltage?.[key], "", 1)}
            </TableCell>
            <TableCell sx={{ py: 0.75, textAlign: "right" }}>
              {fmt(live?.current?.[key], "")}
            </TableCell>
            <TableCell sx={{ py: 0.75, textAlign: "right" }}>
              {fmt(live?.power?.[key], "")}
            </TableCell>
            <TableCell sx={{ py: 0.75, pr: 0, textAlign: "right" }}>
              {fmt(live?.powerFactor?.[key], "", 3)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  const controls = (
    <Stack spacing={2}>
      {!live && (
        <Alert severity="info" variant="outlined">
          {formatMessage("inverter.controls.noInverterDataYet")}
        </Alert>
      )}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography variant="body2">
          {formatMessage("inverter.controls.powerState")}
        </Typography>
        <Switch
          color="success"
          checked={isOn}
          disabled={hasPending || create.isPending || !inverter.isActive}
          onChange={(_, v) =>
            setPending({ type: "set_power_state", state: v ? "on" : "off" })
          }
        />
      </Stack>
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="body2">
            {formatMessage("inverter.controls.activePowerLimit")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {limitDraft}%
            {inverter.capacity != null &&
              limitDraft !== 100 &&
              // capacity is in Watts; formatPower expects kW.
              ` ≈ ${
                formatPower(
                  (inverter.capacity / 1000) * (limitDraft / 100),
                ).formatted
              }`}
          </Typography>
        </Stack>
        <Box sx={{ px: 3 }}>
          <Slider
            value={limitDraft}
            onChange={(_, v) => setLimitDraft(v as number)}
            min={0}
            max={100}
            step={1}
            disabled={!inverter.isActive}
            marks={[{ value: commandedLimit }]}
            valueLabelDisplay="auto"
            sx={{
              "& .MuiSlider-mark, & .MuiSlider-markActive": {
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: "action.disabled",
                opacity: 1,
                transform: "translate(-50%, -50%)",
              },
            }}
          />
        </Box>
        <Collapse in={limitDraft !== commandedLimit}>
          <Box sx={{ display: "flex", justifyContent: "center", pt: 1 }}>
            <Button
              size="small"
              variant="contained"
              disabled={hasPending || create.isPending || !inverter.isActive}
              onClick={() =>
                setPending({ type: "set_active_power_limit", percent: limitDraft })
              }
            >
              {formatMessage("inverter.controls.apply")}
            </Button>
          </Box>
        </Collapse>
      </Box>
    </Stack>
  );

  const history = (
    <Stack spacing={1}>
      {isLoading ? (
        <CircularProgress size={16} />
      ) : !commands || commands.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {formatMessage("inverter.history.empty")}
        </Typography>
      ) : (
        commands.slice(0, 8).map((c) => (
          <Stack
            key={c.id}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack>
              <Typography variant="body2">{describeCommand(c)}</Typography>
              <Typography variant="caption" color="text.secondary">
                {new Date(c.createdAt).toLocaleString(locale)}
              </Typography>
            </Stack>
            <CommandStatusChip cmd={c} />
          </Stack>
        ))
      )}
    </Stack>
  );

  return (
    <>
      <Stack direction="row" gap={1} sx={{ px: 1, pb: 1, flexWrap: "wrap" }}>
        <Chip
          size="small"
          label={formatMessage(`status.${displayStatus}` as const)}
          color={STATUS_COLOR[displayStatus]}
        />
        <Chip size="small" variant="outlined" label={inverter.name} />
        {inverter.capacity != null && (
          <Chip
            size="small"
            variant="outlined"
            label={formatPower(inverter.capacity / 1000).formatted}
          />
        )}
        {!inverter.isActive && (
          <Chip size="small" color="warning" label="disabled" />
        )}
        {live?.date && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>
            {formatMessage("lastUpdate")}: {new Date(live.date).toLocaleTimeString(locale)}
          </Typography>
        )}
      </Stack>

      <Grid2 container spacing={0}>
        <Widget
          size={{ xs: 12, md: canEdit ? 6 : 12 }}
          title={formatMessage("inverter.perPhase")}
          content={perPhase}
        />
        <Widget
          size={{ xs: 12, md: canEdit ? 6 : 12 }}
          title={formatMessage("inverter.readings")}
          content={readings}
        />
        {canEdit && (
          <Widget
            size={{ xs: 12 }}
            title={formatMessage("inverter.controls")}
            content={controls}
          />
        )}
        {canEdit && (
          <Widget
            size={{ xs: 12 }}
            title={formatMessage("inverter.history")}
            content={history}
          />
        )}
      </Grid2>

      <Dialog open={pending !== null} onClose={() => setPending(null)}>
        <DialogTitle>{formatMessage("inverter.confirm.title")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pending && describePending(pending, formatMessage)}
          </DialogContentText>
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary">
            {formatMessage("inverter.confirm.note")}{" "}
            <strong>{inverter.name}</strong>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPending(null)} disabled={create.isPending}>
            {formatMessage("inverter.confirm.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={submit}
            disabled={create.isPending}
          >
            {create.isPending
              ? formatMessage("inverter.confirm.sending")
              : formatMessage("inverter.confirm.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const InverterPage = () => {
  const formatMessage = useFormatMessage();
  const selectedPowerPlant = useAppSelector(getSelectedPowerPlant);
  const canEdit = !!selectedPowerPlant?.canEdit;
  // Per-inverter liveData refreshes on the Pi's cadence — match that here
  // so the page's readings stay fresh. Sidebar/router callers don't pass
  // an interval, so they only fetch on mount (and react-query refetch
  // triggers like window focus).
  const { data: inverters, isLoading, dataUpdatedAt } = useGetInverters(
    selectedPowerPlant?.liveDataInterval ?? 15_000,
  );
  // activeId may not match any current row (initial load, or an inverter
  // was removed). The derive-with-fallback below handles both cases.
  const [activeId, setActiveId] = useState<string | null>(null);

  if (isLoading) {
    return (
      <PageLayout pageTitle={formatMessage("pageTitle.inverter")}>
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  // The route is gated on hasInverters at the router level — landing here
  // with an empty list means the list was emptied mid-session. Redirect
  // home rather than rendering a stub.
  if (!inverters || inverters.length === 0) {
    return <Navigate to={DEFAULT} replace />;
  }

  const active = inverters.find((i) => i.id === activeId) ?? inverters[0];

  return (
    <PageLayout pageTitle={formatMessage("pageTitle.inverter")}>
      {inverters.length > 1 && (
        <Tabs
          value={active.id}
          onChange={(_, v) => setActiveId(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ px: 1, mb: 1 }}
        >
          {inverters.map((i) => (
            <Tab key={i.id} value={i.id} label={i.name} />
          ))}
        </Tabs>
      )}
      <InverterPanel
        inverter={active}
        canEdit={canEdit}
        liveDataInterval={selectedPowerPlant?.liveDataInterval ?? 15_000}
        dataUpdatedAt={dataUpdatedAt}
      />
    </PageLayout>
  );
};
